import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";
import { getTicketFormById, updateTickets } from "../../api/ticketsApi";
import { HttpStatusCode } from "axios";

const UpdateTicket = () => {
    const navigate = useNavigate();
    const notify = useNotification();
    const location = useLocation();
    const [ticketId, setTicketId] = useState(null);
    const [dynamicForm, setDynamicForm] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState({});

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const ticketIdFromUrl = queryParams.get("tid");
        setTicketId(parseInt(ticketIdFromUrl));
        
        if (ticketId > 0) {
            fetchTicketForm(ticketId);
        }
    }, [ticketId]);

    const fetchTicketForm = async (id) => {
        try {
            setLoading(true);
            const res = await getTicketFormById(id);
            if (res.status === HttpStatusCode.Ok) {
                setDynamicForm(res.data);
                console.log(res.data);
                setFormValues({
                    priority: res.data.priorityList?.find((item) => item.key === res.data.priority)?.key || "",
                    progressStatus: res.data.progressStatusList?.find((item) => item.key === res.data.progressStatus)?.key || "",
                    ticketHead: res.data.ticketHead,
                    ticketCategory: res.data.ticketCategoryList?.find((item) => item.key === res.data.ticketCategory)?.key || "",
                    assignee: res.data.assignee || "",
                    details: res.data.details
                });
            } else {
                notify("error", "Failed to load form fields");
                console.error("Error loading form fields:", res);
            }
        } catch (error) {
            notify("error", "Failed to load form fields");
            console.error("API error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const validateForm = (formValues) => {
        const errors = [];
        if (!formValues.priority) {
            errors.push("Priority is required.");
        }
        if (!formValues.progressStatus) {
            errors.push("Progress Status is required.");
        }
        if (!formValues.ticketCategory) {
            errors.push("Ticket Category is required.");
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm(formValues);
        if (errors.length > 0) {
            notify("error", errors.join(" "));
            return;
        }
        setLoading(true);

        const payload = {
            ticketId: parseInt(ticketId),
            priority: formValues.priority ? parseInt(formValues.priority) : undefined,
            progressStatus: formValues.progressStatus ? parseInt(formValues.progressStatus) : undefined,
            ticketCategory: formValues.ticketCategory ? parseInt(formValues.ticketCategory) : undefined,
            assignee: formValues.assignee ? parseInt(formValues.assignee) : undefined,
            details: formValues.details
        };

        try {
            await updateTickets(payload);
            notify("success", "Ticket updated successfully");
        } catch (error) {
            notify("error", "Failed to update the ticket");
        } finally {
            setLoading(false);
        }
    };

    const { priorityList, progressStatusList, ticketCategoryList, assigneeList, customerForm, ticketForm, taskList } = dynamicForm ?? {};

    return (
        <div className="space-y-2">
            <h2 className="text-2xl font-bold mb-6 text-blue-600 border-b border-blue-700 pb-2 bg-[#F1FAFB] sticky top-0 z-10">
                Update Tickets/{ticketId}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Left Column - Customer and Ticket Information */}
                <div className="space-y-6">
                    {/* Customer Information Section */}
                    <div className="p-6 bg-white rounded-lg shadow-md border border-blue-100 hover:shadow-xl transition-shadow duration-300 ease-in-out">
                        <h3 className="text-lg font-semibold mb-4 text-blue-600">Customer Information</h3>
                        {customerForm && (
                            <table className="table-auto w-full mb-6 border-collapse border border-gray-200">
                                <tbody>
                                    {Object.entries(customerForm).map(([key, value]) => (
                                        <tr key={key} className="border-b border-gray-200">
                                            <td className="px-4 py-2 font-medium text-gray-700 capitalize">
                                                {key.replace(/([A-Z])/g, " $1")}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600">{value || "N/A"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Ticket Information Section */}
                    <div className="p-6 bg-white rounded-lg shadow-md border border-orange-100 hover:shadow-xl transition-shadow duration-300 ease-in-out">
                        <h3 className="text-lg font-semibold mb-4 text-orange-600">Ticket Information/{formValues.ticketHead}</h3>
                        {ticketForm && (
                            <table className="table-auto w-full mb-6 border-collapse border border-gray-200">
                                <tbody>
                                    {Object.entries(ticketForm).map(([key, value]) => (
                                        <tr key={key} className="border-b border-gray-200">
                                            <td className="px-4 py-2 font-medium text-gray-700 capitalize">
                                                {key.replace(/([A-Z])/g, " $1")}
                                            </td>
                                            <td className="px-4 py-2 text-gray-600">{value || "N/A"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

                {/* Center Column - Form Section */}
                <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto border border-blue-200 hover:shadow-xl transition-shadow duration-300 ease-in-out">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <h3 className="text-lg font-semibold text-blue-600">Update Ticket Information</h3>
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                Description
                            </label>
                            <textarea
                                id="details"
                                name="details"
                                value={formValues.details}
                                onChange={handleInputChange}
                                rows="4"
                                className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter the task new details here..."
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="ticketCategory" className="block text-sm font-medium text-gray-700">
                                    Ticket Category
                                </label>
                                <select
                                    id="ticketCategory"
                                    name="ticketCategory"
                                    value={formValues.ticketCategory || ""}
                                    onChange={handleInputChange}
                                    className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="" disabled>
                                        Select Category
                                    </option>
                                    {ticketCategoryList?.map((item) => (
                                        <option key={item.key} value={item.key}>
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                                    Priority
                                </label>
                                <select
                                    id="priority"
                                    name="priority"
                                    value={formValues.priority || ""}
                                    onChange={handleInputChange}
                                    className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-orange-500 focus:border-orange-500"
                                >
                                    <option value="" disabled>
                                        Select Priority
                                    </option>
                                    {priorityList?.map((item) => (
                                        <option key={item.key} value={item.key}>
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-4">
                            <div>
                                <label htmlFor="progressStatus" className="block text-sm font-medium text-gray-700">
                                    Progress
                                </label>
                                <select
                                    id="progressStatus"
                                    name="progressStatus"
                                    value={formValues.progressStatus || ""}
                                    onChange={handleInputChange}
                                    className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-orange-500 focus:border-orange-500"
                                >
                                    <option value="" disabled>
                                        Select Progress
                                    </option>
                                    {progressStatusList?.map((item) => (
                                        <option key={item.key} value={item.key}>
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="assignee" className="block text-sm font-medium text-gray-700">
                                    Assign To
                                </label>
                                <select
                                    id="assignee"
                                    name="assignee"
                                    value={formValues.assignee || ""}
                                    onChange={handleInputChange}
                                    className="block w-full px-4 py-2 mt-1 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="" disabled>
                                        Select Assignee
                                    </option>
                                    {assigneeList?.map((item) => (
                                        <option key={item.key} value={item.key}>
                                            {item.value}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="mt-6 px-6 py-3 bg-blue-500 text-white font-semibold rounded-md shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                            disabled={loading}
                        >
                            {loading ? "Updating..." : "Update"}
                        </button>
                    </form>
                </div>

                {/* Right Column - Activity Section */}
                <div className="p-6 bg-white rounded-lg shadow-lg max-w-2xl mx-auto border border-orange-200 hover:shadow-xl transition-shadow duration-300 ease-in-out">
                    {taskList && taskList.length > 0 ? (
                        <div>
                            <h3 className="text-2xl font-semibold text-orange-600 mb-4">Activity</h3>
                            <div className="space-y-4">
                                {taskList.map((task, index) => (
                                    <div key={index} className="p-5 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 ease-in-out">
                                        <p className="text-gray-800 text-lg font-medium">{task.details}</p>
                                        <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                                            <span className="flex items-center space-x-2">
                                                <span>By {task.updatedBy}</span> <span>{task.updatedAt}</span>
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-gray-500">No activity found for this ticket.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpdateTicket;
